import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import companiesService from "../features/companies/companiesService";
import MainLayout from "../components/layout/MainLayout";
import LoginPage from "../pages/auth/LoginPage";
import RegisterPage from "../pages/auth/RegisterPage";
import DashboardPage from "../pages/dashboard/DashboardPage";
import UsersPage from "../pages/users/UsersPage";
import CompaniesPage from "../pages/companies/CompaniesPage";
import MarketingDashboard from "../pages/dashboard/MarketingDashboard";
import AIChatbotsPage from "../pages/ai_chatbots/AIChatbotsPage";
import AIChatbotTrainingPage from "../pages/ai_chatbots/AIChatbotTrainingPage";
import CommunicationsHubPage from "../pages/marketing_dashboard/CommunicationsHubPage";
import CompanyResearchPage from "../pages/marketing_dashboard/CompanyResearchPage";
import EmailMarketingPage from "../pages/marketing_dashboard/EmailMarketingPage";
import LeadGenerationPage from "../pages/marketing_dashboard/LeadGenerationPage";
import SocialMediaManagement from "../pages/marketing_dashboard/SocialMediaManagement";
import WhatsappChatbotsPage from "../pages/marketing_dashboard/WhatsappChatbotsPage";
import WhatsappMarketingPage from "../pages/marketing_dashboard/WhatsappMarketingPage";
import AgencyProfile from "../pages/my_agency/AgencyProfile";
import ClientCompaniesPage from "../pages/my_agency/ClientCompaniesPage";
import MyTeamPage from "../pages/my_agency/MyTeamPage";
import UsagePage from "../pages/usage/UsagePage";
import AIToolsPage from "../pages/ai_tools/AIToolsPage";
import AIToolsChatbotsPage from "../pages/ai_tools/AIToolsChatbotsPage";
import DeepResearchPage from "../pages/ai_tools/DeepResearchPage";
import WhatsappChatbotsToolsPage from "../pages/ai_tools/WhatsappChatbotsPage";
// ... diğer importlar

// Auth kontrolü için koruyucu bileşen
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = localStorage.getItem("token") !== null;
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isChecking, setIsChecking] = useState(true);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsChecking(false);
      return;
    }

    // Allow profile setup pages to render without redirect loop
    const pathname = location.pathname;
    if (
      pathname.startsWith("/my-agency/profile") ||
      pathname.startsWith("/my-company/profile")
    ) {
      setRedirectTo(null);
      setIsChecking(false);
      return;
    }

    const roles = user?.roles || [];
    const isAgencyOwner = roles.includes("agency_owner");
    const isCompanyOwner = roles.includes("company_owner");
    const requiresCompany = isAgencyOwner || isCompanyOwner;

    if (!requiresCompany) {
      setRedirectTo(null);
      setIsChecking(false);
      return;
    }

    const checkCompany = async () => {
      try {
        // honor local storage hint to avoid flicker
        const setupHint = localStorage.getItem('companySetupRequired');
        if (setupHint === 'agency') {
          throw new Error('setup-required');
        }
        if (setupHint === 'single') {
          throw new Error('setup-required');
        }
        if (isAgencyOwner) {
          await companiesService.getMyAgency();
        } else if (isCompanyOwner) {
          await companiesService.getMyCompany();
        }
        setRedirectTo(null);
      } catch (e) {
        // persist setup requirement to avoid flicker on sidebar clicks
        localStorage.setItem('companySetupRequired', isAgencyOwner ? 'agency' : 'single');
        setRedirectTo(
          isAgencyOwner ? "/my-agency/profile?type=agency" : "/my-company/profile?type=single"
        );
      } finally {
        setIsChecking(false);
      }
    };

    checkCompany();
  }, [isAuthenticated, location.pathname, user]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isChecking) {
    return null;
  }

  if (redirectTo && location.pathname !== redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="marketing-dashboard" element={<MarketingDashboard />} />
        <Route
          path="marketing-dashboard/communications-hub"
          element={<CommunicationsHubPage />}
        />
        <Route
          path="marketing-dashboard/company-research"
          element={<CompanyResearchPage />}
        />
        <Route
          path="marketing-dashboard/email-marketing"
          element={<EmailMarketingPage />}
        />
        <Route
          path="marketing-dashboard/lead-generation"
          element={<LeadGenerationPage />}
        />
        <Route
          path="marketing-dashboard/social-media-management"
          element={<SocialMediaManagement />}
        />
        <Route
          path="marketing-dashboard/whatsapp-chatbots"
          element={<WhatsappChatbotsPage />}
        />
        <Route
          path="marketing-dashboard/whatsapp-marketing"
          element={<WhatsappMarketingPage />}
        />
        <Route path="ai-chatbots" element={<AIChatbotsPage />} />
        <Route path="ai-chatbots/:id/training" element={<AIChatbotTrainingPage />} />

        {/* Chatbot routes */}
        <Route path="chatbots">
          <Route index element={<div>Chatbots List</div>} />
          <Route path="new" element={<div>Create New Chatbot</div>} />
          <Route path=":id" element={<div>Chatbot Details</div>} />
          <Route path=":id/edit" element={<div>Edit Chatbot</div>} />
          <Route path="analytics" element={<div>Chatbot Analytics</div>} />
        </Route>

        {/* Company routes */}
        <Route path="companies">
          <Route index element={<CompaniesPage />} />
          <Route path="new" element={<CompaniesPage isNew={true} />} />
          <Route path=":id" element={<CompaniesPage isDetail={true} />} />
          <Route path=":id/edit" element={<CompaniesPage isEdit={true} />} />
        </Route>

        {/* Report routes */}
        <Route path="reports">
          <Route index element={<div>Reports</div>} />
          <Route path="generate" element={<div>Generate Report</div>} />
        </Route>

        {/* Usage route */}
        <Route path="usage" element={<UsagePage />} />

        {/* AI Tools */}
        <Route path="ai-tools">
          <Route index element={<AIToolsPage />} />
          <Route path="ai-chatbots" element={<AIToolsChatbotsPage />} />
          <Route path="deep-research" element={<DeepResearchPage />} />
          <Route path="whatsapp-chatbots" element={<WhatsappChatbotsToolsPage />} />
        </Route>

        {/* Settings route */}
        <Route path="settings" element={<div>Settings</div>} />

        {/* Profile route */}
        <Route path="profile" element={<div>User Profile</div>} />

        {/* My Agency routes */}
        <Route path="my-agency">
          <Route index element={<div>My Agency</div>} />
          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <AgencyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="my-team"
            element={
              <ProtectedRoute>
                <MyTeamPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* My Company routes for company_owner (reuse agency components) */}
        <Route path="my-company">
          <Route index element={<Navigate to="profile" replace />} />
          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <AgencyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="my-team"
            element={
              <ProtectedRoute>
                <MyTeamPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Client Companies routes */}
        <Route
          path="client-companies"
          element={
            <ProtectedRoute>
              <ClientCompaniesPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all - 404 */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AppRoutes;
